<template>
  <div
    v-if="currentTrack !== null"
    v-show="!disableThePlayer"
    id="player"
  >
    <div class="columns is-vcentered is-mobile">
      <div class="column is-narrow">
        <router-link :to="trackArtistLinksDisabled ? '' : trackRouteTo">
          <img
            style="height: 100px;"
            :src="track.cover_thumbnail"
          >
        </router-link>
      </div>
      <div
        v-if="track"
        class="column is-one-fifth"
      >
        <p
          class="text label-medium color-white"
          style="margin: 0"
        >
          <router-link :to="trackArtistLinksDisabled ? '' : trackRouteTo">
            <template v-if="isSfx">
              <span class="is-hidden-mobile">
                {{ track.description }}
              </span>
              <span class="is-hidden-tablet is-hidden-desktop">
                {{ track.shortDescription }}
              </span>
            </template>
            <template v-else>
              {{ track.display_title }}
            </template>
            <span
              v-if="track.explicit == 'Explicit' || track.explicit == true"
              class="tag explicit"
            >Explicit</span>
          </router-link>
        </p>
        <p
          v-if="track.featuring_title"
          class="text label-small color-white is-hidden-mobile"
          style="margin: 0"
        >
          <router-link :to="trackArtistLinksDisabled ? '' : trackRouteTo">
            {{ track.featuring_title }}
          </router-link>
        </p>
        <p
          class="text small color-bluegrey4"
          :class="{ 'is-hidden-mobile': isSfx }"
        >
          <router-link :to="trackArtistLinksDisabled ? '' : artistRouteTo">
            {{ track.artist.name }}
          </router-link>
        </p>
      </div>
      <div
        v-if="track && isTrack"
        class="column is-narrow"
      >
        <b-tooltip
          v-if="track.instrumental_version"
          label="Instrumental version available"
          type="is-dark"
          position="is-top"
        >
          <router-link :to="trackRouteTo">
            <span
              class="icon-static small plus-circle"
              style="margin: -4px 0 -4px 0;"
            />
          </router-link>
        </b-tooltip>
      </div>
      <div class="column is-narrow">
        <div class="buttons is-grouped">
          <a
            :disabled="!showControls || !hasPrev"
            class="icon-button medium player-skip-prev"
            @click.prevent="prev"
          />
          <a
            v-if="isPlaying"
            :disabled="!showControls || track === null"
            class="icon-button large player-pause"
            @click.prevent="playPause"
          />
          <a
            v-if="!isPlaying"
            :disabled="!showControls || track === null"
            class="icon-button large player-play"
            @click.prevent="playPause"
          />
          <a
            :disabled="!showControls || !hasNext"
            class="icon-button medium player-skip-next"
            @click.prevent="next"
          />
        </div>
      </div>
      <div class="column is-hidden-touch">
        <div class="columns is-gapless is-vcentered">
          <div class="column is-narrow">
            <div
              class="text tiny color-bluegrey4"
              style="width: 30px; margin-right: 4px;"
            >
              {{ currentTime|formattedDuration }}
            </div>
          </div>
          <div class="column">
            <div :id="`waveform-${id}`" />
          </div>
          <div class="column is-narrow">
            <div
              class="text tiny color-bluegrey4"
              style="margin-left: 4px;"
            >
              {{ track.duration|formattedDuration }}
            </div>
          </div>
        </div>
      </div>

      <div class="column is-narrow is-hidden-touch">
        <div class="columns is-gapless is-vcentered">
          <div class="column is-narrow volume-level">
            <a
              v-if="!isMuted"
              :disabled="!showControls"
              class="icon-button medium player-volume"
              @click.prevent="toggleMute"
            />
            <a
              v-if="isMuted"
              :disabled="!showControls"
              class="icon-button medium player-muted"
              @click.prevent="toggleMute"
            />
          </div>
          <div class="column is-narrow">
            <b-slider
              v-model="volumeLevel"
              rounded
              size="is-small"
              :tooltip="false"
              :min="0"
              :max="1"
              :step="0.01"
              :value="1"
            />
          </div>
        </div>
      </div>
      <div
        v-if="track"
        class="column is-narrow actions is-hidden-touch"
      >
        <div
          v-if="isTrack"
          class="buttons are-small"
        >
          <b-tooltip
            label="Add to collection"
            type="is-dark"
            position="is-top"
          >
            <a
              class="icon-button plus-inverse"
              :disabled="!isLoggedIn"
              @click="handleAddToCollection"
            />
          </b-tooltip>
          <b-tooltip
            label="Remove from favourites"
            type="is-dark"
            position="is-top"
          >
            <a
              v-show="isFavourite"
              class="icon-button heart-active"
              @click="handleFavouriteDelete"
            />
          </b-tooltip>
          <b-tooltip
            label="Add to favourites"
            type="is-dark"
            position="is-top"
          >
            <a
              v-show="!isFavourite"
              class="icon-button heart-inverse"
              @click="handleFavouriteCreate"
            />
          </b-tooltip>
          <b-tooltip
            label="Download preview"
            type="is-dark"
            position="is-top"
          >
            <a
              class="icon-button preview-inverse"
              @click="handleShowPreviewModal"
            />
          </b-tooltip>
          <b-tooltip
            label="Similar songs"
            type="is-dark"
            position="is-top"
          >
            <router-link
              class="icon-button link-inverse"
              :to="trackRouteTo"
              @click.native="logSimilarSongsAnalytics"
            />
          </b-tooltip>
          <b-tooltip
            label="License"
            type="is-dark"
            position="is-top"
          >
            <b-dropdown
              v-if="isLoggedIn"
              class="is-top-left"
              aria-role="list"
            >
              <a
                slot="trigger"
                class="icon-button shopping-cart-inverse"
              />

              <b-dropdown-item
                aria-role="listitem"
                @click="handleAddToCart"
              >
                <i class="icon-button shopping-cart">
                  <span style="font-style: normal; padding-left: 32px;">Add to cart</span>
                </i>
              </b-dropdown-item>

              <b-dropdown-item
                aria-role="listitem"
                @click="handleShowSubscriptionDownload"
              >
                <i class="icon-button download">
                  <span style="font-style: normal; padding-left: 32px;">Subscription</span>
                </i>
              </b-dropdown-item>
            </b-dropdown>
            <a
              v-if="!isLoggedIn"
              class="icon-button download-inverse"
              @click="$store.dispatch('common/startSignupWorkflow', { reason: 'download', track })"
            />
          </b-tooltip>
        </div>
        <div
          v-if="isSfx"
          class="buttons are-small"
        >
          <b-tooltip
            :label="sfxDownloadButtonText"
            type="is-dark"
            position="is-left"
          >
            <a
              class="icon-button download-inverse"
              @click="handleSfxDownload"
            />
          </b-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WaveSurfer from 'wavesurfer.js'
import sfxClient from '@/api/sfx.js'
import favouritesClient from '@/api/favouriteTracks.js'
import waveformClient from '@/api/waveforms.js'
import { formatComponentName } from '@/utils/common.js'
import { logSimilarSongsClick, logTrackListen } from '@/utils/segment.js'
import { addTrackToCart, showSubscriptionDownload, showPreviewDownload } from '@/utils/trackLicensing.js'

export default {
  name: 'ThePlayer',

  data () {
    return {
      id: null,
      wavesurferReady: false,
      wavesurfer: null,
      waveData: null,
      track: null,
      volumeLevel: 1,
      isMuted: false,
      currentTime: 0,
      creatingFavourite: false,
      deletingFavourite: false,
      listeningIntervalId: null,
      listeningIntervalTime: 10000,
      loadRequestId: 0
    }
  },

  computed: {
    // Routes can set meta.disableThePlayer to stop playback and hide the bar, e.g. the
    // full-page sign-up and subscribe flow. The bar is hidden rather than removed so the
    // player still works when the user leaves.
    disableThePlayer: function () {
      return this.$route.meta.disableThePlayer === true
    },

    componentName: function () {
      return formatComponentName(this)
    },

    isLoggedIn: function () {
      return this.$store.getters['auth/isLoggedIn']
    },

    currentSubscription: function () {
      return this.$store.getters['subscriptions/currentSubscription']
    },

    isTrack: function () {
      return this.track.item_type === 'track'
    },

    isSfx: function () {
      return this.track.item_type === 'sfx'
    },

    showControls: function () {
      return this.wavesurferReady
    },

    playPauseLabel: function () {
      if (this.wavesurfer === null) {
        return '...'
      }
      return this.$store.state.player.isPlaying ? 'Pause' : 'Play'
    },

    isPlaying: function () {
      return this.$store.state.player.isPlaying
    },

    muteLabel: function () {
      if (this.wavesurfer === null) {
        return '...'
      }
      return this.isMuted ? 'Unmute' : 'Mute'
    },

    hasPrev: function () {
      return this.$store.getters['player/hasPrev']
    },

    hasNext: function () {
      return this.$store.getters['player/hasNext']
    },

    currentTrack: function () {
      return this.$store.getters['player/currentTrack']
    },

    playbackRequest: function () {
      return this.$store.getters['player/playbackRequest']
    },

    isFavourite: function () {
      if (this.isLoggedIn) {
        if (this.deletingFavourite) {
          return false
        }
        return this.creatingFavourite || this.$store.getters['favouriteTracks/isFavourite'](this.track.pk)
      }
      return false
    },

    trackRouteTo: function () {
      if (this.track && this.track.item_type !== 'track') {
        // If this is not a track, we don't have a track detail page.
        return { name: 'not-found' }
      }
      return {
        name: 'track-detail',
        params: {
          slug: this.track.slug,
          artistSlug: this.track.artist.slug,
          trackId: this.track.pk
        }
      }
    },

    artistRouteTo: function () {
      if (this.track && this.track.item_type !== 'track') {
        // If this is not a track, we don't have an artist detail page.
        return { name: 'not-found' }
      }
      return {
        name: 'artist-detail',
        params: {
          slug: this.track.artist.slug,
          artistId: this.track.artist.pk
        }
      }
    },

    artistTrackLabel: function () {
      if (!this.track) {
        return ''
      }
      if (this.track.item_type === 'track') {
        return `${this.track.artist.name} - ${this.track.display_title}`
      }
      if (this.track.item_type === 'sfx') {
        return this.track.description
      }
      return ''
    },

    trackArtistLinksDisabled: function () {
      return this.isSfx
    },

    disableSfxDownload: function () {
      return this.isLoggedIn && this.currentSubscription && !this.currentSubscription.includes_sfx
    },

    sfxDownloadButtonText: function () {
      if (this.disableSfxDownload) {
        return 'Contact support to upgrade and get SFX'
      }
      return 'Download'
    }
  },

  watch: {
    disableThePlayer: function (disabled) {
      if (disabled && this.wavesurfer !== null && this.wavesurfer.isPlaying()) {
        this.wavesurfer.pause()
      }
    },

    currentTrack: function () {
      const track = this.$store.getters['player/currentTrack']
      if (track === null || track === undefined) {
        return
      }

      if (this.track === null || track.pk !== this.track.pk) {
        this.track = this.currentTrack
        // Load audio after next DOM update otherwise we can't access the
        // (until then) hidden waveform div.
        this.$nextTick(() => {
          this.loadTrack()
        })
      }
    },

    playbackRequest: function (request) {
      if (request === null) {
        return
      }
      if (this.wavesurfer !== null && (request === 'play') !== this.isPlaying) {
        this.playPause()
      }
      // Always clear it, so the same request made again is seen as a change.
      this.$store.dispatch('player/setPlaybackRequest', null)
    },

    volumeLevel: function () {
      if (this.wavesurfer !== null) {
        if (this.isMuted) {
          this.toggleMute()
        }
        this.wavesurfer.setVolume(this.volumeLevel)
      }
    }
  },

  created () {
    this.id = this._uid
    // Added once here rather than per track load, otherwise each load stacks another
    // listener and a single press toggles play/pause several times.
    document.body.addEventListener('keypress', this.handleSpacebar)
  },

  beforeDestroy () {
    document.body.removeEventListener('keypress', this.handleSpacebar)
  },

  methods: {
    handleSpacebar: function (ev) {
      // Leave space alone (e.g. for page scrolling) until a track has been loaded, and
      // while the player is hidden.
      if (ev.key !== ' ' || this.wavesurfer === null || this.disableThePlayer) {
        return
      }
      // If the active element is a form input we don't want to play/pause, we want a space!
      const inputTags = ['input', 'select', 'textarea']
      if (document.activeElement && inputTags.indexOf(document.activeElement.tagName.toLowerCase()) > -1) {
        return
      }
      ev.preventDefault()
      this.playPause()
    },

    prev: function (event) {
      if (this.$store.getters['player/isPlaybackBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
        return
      }
      if (this.$store.getters['player/isPlaybackSoftBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
      }
      this.$store.dispatch('player/setPlayAfterLoad', true)
      this.$store.dispatch('player/prevTrack')
    },

    next: function () {
      if (this.$store.getters['player/isPlaybackBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
        return
      }
      if (this.$store.getters['player/isPlaybackSoftBlocked']) {
        this.$store.dispatch('common/startSignupWorkflow', { reason: 'play', track: this.track })
      }
      this.$store.dispatch('player/setPlayAfterLoad', true)
      this.$store.dispatch('player/nextTrack')
    },

    // Let track list mini-waveforms show how far through the current track we are.
    shareProgress: function () {
      const duration = this.wavesurfer.getDuration()
      if (duration > 0) {
        this.$store.dispatch('player/setPlayProgress', this.wavesurfer.getCurrentTime() / duration)
      }
    },

    playPause: function () {
      if (this.wavesurfer !== null) {
        this.wavesurfer.playPause()
      }
    },

    initWavesurfer: function () {
      const waveSelector = `#waveform-${this.id}`

      if (this.wavesurfer !== null) {
        this.wavesurfer.destroy()
      }

      this.wavesurfer = WaveSurfer.create({
        backend: 'MediaElement',
        mediaType: 'audio',
        container: waveSelector,
        waveColor: 'rgba(114,121,134,0.7)',
        progressColor: '#FF6C6C',
        cursorColor: '#fff',
        barWidth: 2,
        height: 36,
        responsive: true,
        normalize: this.track.item_type !== 'track' // false for tracks; true for sfx
      })
      this.wavesurfer.setVolume(this.volumeLevel)

      const vm = this
      this.wavesurfer.on('finish', function () {
        // Play through lists of tracks, but not sound effects, which are auditioned one
        // at a time. (This used to be a flag each page set, which leaked between pages.)
        if (vm.hasNext && vm.isTrack) {
          vm.next()
        }
      })

      // Unbind event handlers on error.
      this.wavesurfer.on('error', function () {
        vm.wavesurfer.unAll()
        clearInterval(vm.listeningIntervalId)
      })

      // Keep track of the track play progress as the track is playing.
      // `audioprocess` fires continuously as the audio plays and on seeking.
      this.wavesurfer.on('audioprocess', function () {
        vm.currentTime = vm.wavesurfer.getCurrentTime()
        vm.shareProgress()
      })
      // Also share seeks made while paused, which don't fire `audioprocess`.
      this.wavesurfer.on('seek', function () {
        vm.shareProgress()
      })

      // Update state that monitors whether we're playing a track or not.
      // This is done as an event (as opposed to in this.playPause) so we
      // track the state properly when the play/pause command came from
      // elsewhere (e.g. hardware media controls).
      this.wavesurfer.on('pause', function () {
        vm.$store.dispatch(
          'player/setIsPlaying',
          vm.wavesurfer.isPlaying()
        )
        clearInterval(vm.listeningIntervalId)
      })

      this.wavesurfer.on('play', function () {
        vm.$store.dispatch(
          'player/setIsPlaying',
          vm.wavesurfer.isPlaying()
        )

        // Log that the user has listened to some of the track.
        vm.listeningIntervalId = setInterval(() => {
          logTrackListen(vm.track.artist.name, vm.track.display_title, vm.listeningIntervalTime / 1000)
        }, vm.listeningIntervalTime)
      })

      // Handle physical media control prev/next presses.
      if (navigator.mediaSession) {
        navigator.mediaSession.setActionHandler('nexttrack', function () {
          if (!vm.wavesurfer) {
            return
          }

          if (vm.hasNext) {
            vm.next()
          }
        })
        navigator.mediaSession.setActionHandler('previoustrack', function () {
          if (!vm.wavesurfer) {
            return
          }

          // If we're 5 seconds or more into a track, or there is no previous track,
          // seek to start of current track.
          if (vm.currentTime > 5 || !vm.hasPrev) {
            vm.wavesurfer.seekTo(0)
            return
          }
          if (vm.hasPrev) {
            vm.prev()
          }
        })
      }
    },

    loadTrack: function ({ startPlayback = false } = {}) {
      const requestId = ++this.loadRequestId
      const audioUri = (
        this.track.item_type === 'track'
          ? this.track.streaming_m4a
          : this.track.previewUrl // We are assuming this is a PSE sfx.
      )

      if (!audioUri) {
        console.error('Missing audio URI for track', this.track.pk)
        return
      }

      // Make sure that listening logging for a previous track is halted.
      clearInterval(this.listeningIntervalId)

      this.$store.dispatch('player/setPlayProgress', 0)
      this.wavesurferReady = false
      this.initWavesurfer()

      // Function to set up wavesurfer. Needs to be called from a promise in case of sfx.
      const configureWavesurfer = (audioUri) => {
        if (requestId !== this.loadRequestId || this.wavesurfer === null) {
          return
        }
        this.wavesurfer.load(audioUri, this.waveData)
        this.wavesurfer.setMute(this.isMuted)
        if (
          this.$store.state.player.playAfterLoad === true ||
          startPlayback === true
        ) {
          this.wavesurfer.play()
          this.$store.dispatch('player/setPlayAfterLoad', false)
        }
        this.wavesurferReady = true
        this.$store.dispatch('player/setIsPlaying', this.wavesurfer.isPlaying())
      }

      if (this.track.item_type === 'track') {
        // Load higher resolution waveform data from API (as opposed to the one
        // included in search results). This is because for tracks we want to show
        // a more detailed waveform in the player whereas for smaller waveforms in
        // the search results we can get away with a lower resolution one.

        // Check if `waveform` is an object with a `pk`, if it is use that, otherwise
        // we assume that `waveform` IS a pk.
        const waveformPk = this.track.waveform.pk || this.track.waveform
        if (!waveformPk) {
          this.waveData = null
          configureWavesurfer(audioUri)
          return
        }
        waveformClient.retrieve(waveformPk).then(response => {
          if (requestId !== this.loadRequestId) {
            return
          }
          this.waveData = response.data.data
          configureWavesurfer(audioUri)
        })
      } else if (this.track.item_type === 'sfx') {
        // Load json waveform file and store on component.
        fetch(this.track.waveformDataUrl)
          .then(response => {
            response.json()
              .then(data => {
                if (requestId !== this.loadRequestId) {
                  return
                }
                this.waveData = data.data
                configureWavesurfer(audioUri)
              })
          })
          .catch(() => {
            console.error('Failed to load waveform data for SFX', this.track.pk)
            this.waveData = null // Fallback to no waveform data.
            configureWavesurfer(audioUri)
          })
      }
    },

    toggleMute: function () {
      if (this.wavesurfer !== null) {
        const muteValue = !this.wavesurfer.getMute()
        this.wavesurfer.setMute(muteValue)
        this.isMuted = muteValue
      }
    },

    handleAddToCollection () {
      if (this.isLoggedIn) {
        this.$store.dispatch(
          'collections/setTrackAddingToCollection',
          this.track
        )
        this.$store.dispatch('collections/showCollectionsModal')
      } else {
        this.$store.dispatch('user/showSignupModal', { reason: 'collection', track: this.track })
      }
    },

    handleFavouriteCreate () {
      if (this.isLoggedIn) {
        this.creatingFavourite = true
        favouritesClient
          .create({ item_pk: this.track.pk })
          .then(response => {
            this.$store.dispatch('favouriteTracks/appendToFavouriteTracks', response.data)
              .then(() => {
                this.creatingFavourite = false
              })
          })
          .catch(() => {
            this.creatingFavourite = false
          })
      } else {
        this.$store.dispatch('user/showSignupModal', { reason: 'favourite', track: this.track })
      }
    },

    handleFavouriteDelete () {
      this.deletingFavourite = true

      const favePk = this.$store.getters['favouriteTracks/getFavouritePkFromTrackPk'](this.track.pk)
      favouritesClient.delete(favePk)
        .then(response => {
          this.$store.dispatch('favouriteTracks/removeFromFavouriteTracks', favePk)
            .then(() => {
              this.deletingFavourite = false
            })
        })
        .catch(() => {
          this.deletingFavourite = false
        })
    },

    handleAddToCart () {
      return addTrackToCart(this, this.track)
    },

    handleShowSubscriptionDownload () {
      showSubscriptionDownload(this, this.track)
    },

    handleShowPreviewModal () {
      showPreviewDownload(this, this.track)
    },

    handleSfxDownload () {
      if (this.disableSfxDownload) {
        return
      }
      if (this.isLoggedIn) {
        if (this.currentSubscription && this.currentSubscription.includes_sfx) {
          sfxClient.downloadPseSfx(this.track.pk)
            .then((response) => {
              window.location = response.data.downloadUrl
            })
            .catch((error) => console.log(error))
        } else {
          this.$store.dispatch('subscriptions/showPlanSelectionModal')
        }
      } else {
        this.$store.dispatch('user/showSignupModal')
      }
    },

    logSimilarSongsAnalytics () {
      logSimilarSongsClick(this.$options.name)
    }
  }
}
</script>

<style scoped lang="scss">
@import "../assets/scss/_variables.scss";
  .tag {
    &.explicit {
      font-size: 8px;
      letter-spacing: 0.07em;
      padding: 4px;
      border: 1px solid #f14668 !important; // For some reason using $red doesn't give the correct value!
      background-color: inherit !important;
      color: #f14668;
      height: 14px;
      margin-left: 2px;
      text-transform: uppercase;
    }
    &.small {
      padding: 0 3px !important;
      border-radius: 6px;
      line-height: 100%;
      height: 18px;
      font-size: $size-8;
      margin: 1px 2px !important;
    }
  }
</style>
